import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ImageUploader from 'react-images-upload';
import DatePicker from "react-datepicker";

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Geocoder from '../Geocoder/Geocoder';

import Helper from '../../helpers/helper';

import { hideModal } from '../../actions/modal/modal';
import { setSidebarItem } from '../../actions/sidebar/sidebar';
import { addKoalaSighting } from '../../actions/mainview/mainview';

import { mainviewActions, modalActions, MAPBOX_GEOCODE_URL } from '../../helpers/constants';

const AddSightingForm = () => {

  const dispatch = useDispatch(),
        [usingGeolocation, setUsingGeolocation] = useState(false),
        [geocodedPlace, setGeocodedPlace] = useState(null),
        appProps = useSelector(store => store.app),
        mainviewProps = useSelector(store => store.mainview),
        formik = useFormik({
          initialValues: {
            location: "",
            datetime_of_sighting: new Date(),
            koalaSex: "Unknown",
            koalaStatus: "Healthy",
            isWithJoey: false,
            isVerified: false,
            treeSpecies: "",
            more_information: "",
            photo: "",
            latitude: "",
            longitude: ""
          },
          validationSchema: Yup.object({
            latitude: Yup.string().required('A sighting location is required'),
            longitude: Yup.string().required('A sighting location is required'),
            postal_code: Yup.string().matches(/^\d+$/, 'Postal code can only contain numbers')
          }),
          onSubmit: values => {

            let form = new FormData();

            form.append('datetime_of_sighting', values.datetime_of_sighting.toISOString());
            form.append('status', values.koalaStatus);
            form.append('sex', values.koalaSex);
            form.append('with_joey', values.isWithJoey);
            form.append('latitude', values.latitude);
            form.append('longitude', values.longitude);
            form.append('verified', values.isVerified);

            if (values.photo !== "") form.append('photo', values.photo);
            if (values.location !== "") form.append('location', values.location);
            if (values.more_information !== "") form.append('more_information', values.more_information);
            if (values.treeSpecies !== "") form.append('tree_species', values.treeSpecies);

            if (appProps.loggedInUser.user.first_name !== "") form.append('first_name', appProps.loggedInUser.user.first_name);
            if (appProps.loggedInUser.user.last_name !== "") form.append('surname', appProps.loggedInUser.user.last_name);
            if (appProps.loggedInUser.user.email !== "") form.append('email', appProps.loggedInUser.user.email);
            if (appProps.loggedInUser.group_name !== "" && appProps.loggedInUser.group_name !== null) form.append('group_name', appProps.loggedInUser.group_name);

            if (appProps.loggedInUser.postal_address !== "" && appProps.loggedInUser.postal_address !== null) form.append('postal_address', appProps.loggedInUser.postal_address);
            if (appProps.loggedInUser.postal_suburb !== "" && appProps.loggedInUser.postal_suburb !== null) form.append('postal_suburb', appProps.loggedInUser.postal_suburb);
            if (appProps.loggedInUser.postal_state !== "" && appProps.loggedInUser.postal_state !== null) form.append('postal_state', appProps.loggedInUser.postal_state);
            if (appProps.loggedInUser.postal_code !== "" && appProps.loggedInUser.postal_code !== null) form.append('postal_code', appProps.loggedInUser.postal_code);

            dispatch(addKoalaSighting(form));
          }
        }),
        isAdmin = appProps.loggedInUser?.is_admin;

  const _modalClose = () => {
    if (!formik.dirty) return dispatch(hideModal());
      
    if (!window.confirm('You have unsaved changes. Closing will lose any changes.\nAre you sure you wish to close?')) return false;

    dispatch(hideModal());
    _cancelClickedLocation();
  }

  const _handleImageChange = e => {
    
    if (e.length < 1) return formik.setFieldValue('photo', null);

    const photo = e[0];

    if (photo.size > 2097152) return Helper.setNotification('danger', 'Image is too big', 'The image you\'ve uploaded is over 2mb in size. Please choose another photo.');
    
    formik.setFieldValue('photo', photo);
    Helper.setNotification('success', 'Success', 'Image successfully uploaded!', true);

  }

  const _useCurrentLocation = () => {
    const pos = mainviewProps.userGeoPosition;

    formik.setFieldValue('longitude', pos.longitude);
    formik.setFieldValue('latitude', pos.latitude);

    _reverseGeocode(pos.latitude, pos.longitude)
    .then(placeName => {
      formik.setFieldValue('location', placeName);
      setGeocodedPlace(placeName);
    });

    formik.errors = {};

    setUsingGeolocation(true);
  }

  const _cancelClickedLocation = () => {

    dispatch({ type: mainviewActions.SELECT_LOCATION_ON_MAP_CANCEL }); // Remove previously clicked location
    setGeocodedPlace(null);

  }

  const _cancelUseGeolocation = () => {
    formik.setFieldValue('latitude', '');
    formik.setFieldValue('longitude', '');

    _cancelClickedLocation();

    setUsingGeolocation(false);
  }

  const _selectLocationOnMap = () => {
    dispatch(setSidebarItem('')); // Hide sidebar ready to intercept any clicks to take them away from continuing sighting location selection flow.

    dispatch({ type: mainviewActions.SELECT_LOCATION_ON_MAP });
    dispatch({
      type: modalActions.SUSPEND_MODAL,
      payload: true
    });

    setUsingGeolocation(false);
  }

  const _reverseGeocode = async (lat, lng) => {

    const reqUrl = `${ MAPBOX_GEOCODE_URL }/${ lng },${ lat }.json?access_token=${ process.env.REACT_APP_Mapbox_API_Key }&country=au`;

    let response = await fetch(reqUrl);
    let json = await response.json();

    return json.features[0]?.place_name;
  }

  useEffect(() => {
    if (mainviewProps.chosenLocation === null) return;

    formik.setFieldValue('latitude', mainviewProps.chosenLocation.lat);
    formik.setFieldValue('longitude', mainviewProps.chosenLocation.lng);

    _reverseGeocode(mainviewProps.chosenLocation.lat, mainviewProps.chosenLocation.lng)
    .then(placeName => {
      formik.setFieldValue('location', placeName);
      setGeocodedPlace(placeName);
    });

    formik.errors = {};
  }, [mainviewProps.chosenLocation]);

  return (
    <>
      <span className="modal__close" onClick={ () => _modalClose() }>&times;</span>

      <form className="form form--restricted" onSubmit={ formik.handleSubmit }>
        <div className="form__group">
          <label className="form__label">Where did you see the Koala?</label>
          {
            geocodedPlace !== null &&
            <p className="form__text" style={{ fontSize: '1rem', margin: '0 0 .5rem .2rem' }}>{ geocodedPlace }</p>
          }

          {
            !usingGeolocation &&
            <>
              {
                geocodedPlace === null &&
                <Geocoder
                  countryLimit="au"
                  isInline
                  className="form__input"
                  onInlineChange={ e => {
                    formik.setFieldValue('location', e.place_name);
                    formik.setFieldValue('longitude', e.center[0]);
                    formik.setFieldValue('latitude', e.center[1]);
                  } }
                  name="location" />
              }
            
              {
                usingGeolocation &&
                <>
                  <p className="form__text" style={{ fontSize: '.75rem', margin: '.2rem 0 0 .2rem' }}>Using your current location (this is only an approximate location).</p>
                  <p className="form__help-text" style={{ margin: '.7rem 0 .2rem .2rem', textAlign: 'left' }}>Remember where you saw the Koala? <span onClick={ () => _cancelUseGeolocation() } className="form__help-link" title="Show the search box to search for where you saw the Koala.">Show the search address box</span>.</p>
                </>
              }

              {
                geocodedPlace !== null &&
                <p className="form__help-text" style={{ margin: '.2rem 0 .2rem .2rem', textAlign: 'left' }}>Want to search for a location? <span onClick={ () => _cancelClickedLocation() } className="form__help-link" title="Want to search for a location? Show search bar.">Show search bar</span>.</p>
              }

              {
                mainviewProps.isGeolocated &&
                <p className="form__help-text" style={{ margin: '.2rem 0 .2rem .2rem', textAlign: 'left' }}>Not sure of the location? <span onClick={ () => _useCurrentLocation() } className="form__help-link" title="Use your location as the position where you saw the Koala.">Use my current location</span>.</p>
              }
            </>
          }
          <p className="form__help-text" style={{ margin: '.2rem 0 .2rem .2rem', textAlign: 'left' }}>Want to choose a location on the map? <span onClick={ () => _selectLocationOnMap() } className="form__help-link" title="Select a location on the map.">Choose here</span>.</p>

          { formik.errors.latitude ? (<span className="form__error-item">{ formik.errors.latitude }</span>) : '' }
        </div>
        <div className="form__group">
          <label className="form__label">When did you see the Koala?</label>
          <DatePicker
            selected={ formik.values.datetime_of_sighting }
            showTimeSelect
            className="form__input"
            name="datetime_of_sighting"
            dateFormat="d/M/yyyy, h:mm aa"
            timeIntervals={ 15 }
            onChange={ date => formik.setFieldValue('datetime_of_sighting', date) } />
        </div>
        <div className="form__group">
          <label htmlFor="koalaSex" className="form__label">What was the Koala's sex?</label>
          <div className="form__select-container">
            <select className="form__select" id="koalaSex" value={ formik.values.koalaSex } onChange={ formik.handleChange } onBlur={formik.handleBlur} name="koalaSex">
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Unknown">Not sure</option>
            </select>
          </div>
        </div>
        <div className="form__group">
          <label htmlFor="koalaStatus" className="form__label">What was the Koala's status?</label>
          <div className="form__select-container">
            <select className="form__select" id="koalaStatus" value={ formik.values.koalaStatus } onChange={ formik.handleChange } onBlur={formik.handleBlur} name="koalaStatus">
              <option value="Healthy">Healthy</option>
              <option value="Sick/Injured">Sick/Injured</option>
              <option value="Deceased">Deceased</option>
            </select>
          </div>
        </div>
        <div className="form__group">
          <label htmlFor="isWithJoey" className="form__label">Was the Koala with a Joey?</label>
          <div className="form__select-container">
            <select className="form__select" id="isWithJoey" value={ formik.values.isWithJoey } onChange={ formik.handleChange } onBlur={formik.handleBlur} name="isWithJoey">
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
        
        {
          isAdmin ?
          (
            <div className="form__group">
              <label htmlFor="isVerified" className="form__label">Verified sighting?</label>
              <div className="form__select-container">
                <select className="form__select" id="isVerified" value={ formik.values.isVerified } onChange={ formik.handleChange } onBlur={ formik.handleBlur } name="isVerified">
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          ) : null
        }

        <div className="form__group">
          <label htmlFor="treeSpecies" className="form__label">What tree species was the Koala on?</label>
          <input type="text" placeholder="What sort of tree was the Koala in" name="treeSpecies" value={formik.values.treeSpecies} onChange={formik.handleChange} onBlur={formik.handleBlur} id="treeSpecies" className="form__input" />
        </div>
        <div className="form__group">
          <label htmlFor="more_information" className="form__label">Any other information?</label>
          <textarea type="text" placeholder="Write about the Koala you've seen&hellip;" value={formik.values.more_information} onChange={formik.handleChange} onBlur={formik.handleBlur} name="more_information" id="more_information" className="form__input form__input--text-area"></textarea>
        </div>
        <div className="form__group">
          <label className="form__label">Add a picture</label>
          <ImageUploader
            withIcon={ true }
            label="Max file size: 2mb. Accepted: jpg|gif|png."
            buttonText='Upload an image'
            onChange={ e => _handleImageChange(e) }
            name="photo"
            withPreview
            id="photo"
            singleImage={ true }
            imgExtension={ ['.jpg', '.gif', '.png', '.gif'] }
            fileSizeError="is over 2mb in size. Please choose a smaller image."
            maxFileSize={ 2097152 } />
        </div>

        <div className="form__button-group">
          <button type="submit" className="form__button button" disabled={ appProps.loading || !formik.dirty }>Add your sighting</button>
        </div>

      </form>

    </>
  );

}

export default AddSightingForm;